/**
 * 내 땅 등록 페이지 (/owner/register)
 * 로그인한 땅주인만 접근 가능
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePageTitle } from "../../hooks/usePageTitle";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { ownerApiFetch } from "../../utils/ownerApi";

const LAND_TYPES = ["전", "답", "임야", "대", "잡종지", "과수원", "공장용지", "창고용지", "기타"];

export function OwnerListingCreate() {
  usePageTitle("내 땅 등록");
  const navigate = useNavigate();
  const m = useIsMobile();

  const [form, setForm] = useState({
    address: "",
    land_type: "",
    area_sqm: "",
    asking_price: "",
    sell_reason: "",
    contact_hours: "",
    notes: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // 로그인 확인
  const ownerInfo = (() => {
    try {
      const s = localStorage.getItem("owner_info");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  })();

  if (!localStorage.getItem("owner_token") || !ownerInfo) {
    return (
      <div style={st.page}>
        <AppHeader />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px", textAlign: "center" }}>
          <div>
            <p style={{ color: "var(--text-secondary)", marginBottom: 20 }}>{"🔒"} 땅주인 로그인이 필요합니다</p>
            <button
              onClick={() => navigate("/owner/login")}
              style={{ padding: "12px 28px", background: "linear-gradient(135deg,#B8972E,#D4AF37)", color: "#0F172A", border: "none", borderRadius: "var(--radius-md)", fontWeight: 700, fontSize: 15, cursor: "pointer" }}
            >
              로그인하기
            </button>
          </div>
        </div>
        <AppFooter />
      </div>
    );
  }

  const update = (key: string, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!form.address || !form.land_type || !form.area_sqm || !form.asking_price) {
      setError("소재지, 지목, 면적, 희망가격은 필수입니다");
      return;
    }

    setLoading(true);
    try {
      await ownerApiFetch("/api/owner/listings", {
        method: "POST",
        body: JSON.stringify({
          address: form.address,
          land_type: form.land_type,
          area_sqm: Number(form.area_sqm),
          asking_price: Number(form.asking_price),
          sell_reason: form.sell_reason,
          contact_hours: form.contact_hours,
          notes: form.notes,
        }),
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "등록에 실패했습니다");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={st.page}>
        <AppHeader />
        <div style={{ flex: 1, display: "flex", flexDirection: "column" as const, alignItems: "center", justifyContent: "center", padding: "40px 20px", textAlign: "center", gap: 20 }}>
          <div style={{ fontSize: 48 }}>{"✅"}</div>
          <h2 style={{ color: "var(--lt-gold)", margin: 0 }}>등록 완료!</h2>
          <p style={{ color: "var(--text-secondary)", margin: 0, lineHeight: 1.6 }}>
            내 땅 정보가 등록되었습니다.<br />
            인증된 중개사가 연결 신청을 하면 연락드리겠습니다.
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={() => setSuccess(false)}
              style={{ padding: "11px 22px", background: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border-medium)", borderRadius: "var(--radius-md)", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
            >
              또 다른 땅 등록
            </button>
            <button
              onClick={() => navigate("/owner/mypage")}
              style={{ padding: "11px 22px", background: "linear-gradient(135deg,#B8972E,#D4AF37)", color: "#0F172A", border: "none", borderRadius: "var(--radius-md)", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
            >
              내 등록 내역 보기
            </button>
          </div>
        </div>
        <AppFooter />
      </div>
    );
  }

  return (
    <div style={st.page}>
      <AppHeader />
      <div style={{ ...st.container, padding: m ? "20px 16px 40px" : "40px 32px 60px" }}>

        <div style={st.pageHeader}>
          <h1 style={st.pageTitle}>{"🌱"} 내 땅 등록</h1>
          <p style={st.pageDesc}>
            {ownerInfo.name}님의 토지 정보를 등록해 주세요. 인증된 중개사에게만 공개됩니다.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column" as const, gap: 20 }}>

          {/* 소재지 + 지목 */}
          <div style={st.section}>
            <h3 style={st.sectionTitle}>토지 기본 정보</h3>
            <div style={st.sectionBody}>
              <div style={{ display: "grid", gridTemplateColumns: m ? "1fr" : "1fr 1fr", gap: 14 }}>
                <div style={{ gridColumn: m ? "1" : "1 / -1" }}>
                  <label style={st.label}>소재지 <span style={{ color: "var(--lt-red)" }}>*</span></label>
                  <input
                    value={form.address}
                    onChange={(e) => update("address", e.target.value)}
                    placeholder="예: 경기도 양평군 옥천면 옥천리 123-4"
                    required
                    style={st.input}
                  />
                </div>
                <div>
                  <label style={st.label}>지목 <span style={{ color: "var(--lt-red)" }}>*</span></label>
                  <select
                    value={form.land_type}
                    onChange={(e) => update("land_type", e.target.value)}
                    required
                    style={st.input}
                  >
                    <option value="">지목 선택</option>
                    {LAND_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={st.label}>면적 (㎡) <span style={{ color: "var(--lt-red)" }}>*</span></label>
                  <input
                    type="number"
                    value={form.area_sqm}
                    onChange={(e) => update("area_sqm", e.target.value)}
                    placeholder="예: 3300"
                    required
                    min={1}
                    style={st.input}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 매각 정보 */}
          <div style={st.section}>
            <h3 style={st.sectionTitle}>매각 정보</h3>
            <div style={st.sectionBody}>
              <div style={{ display: "grid", gridTemplateColumns: m ? "1fr" : "1fr 1fr", gap: 14 }}>
                <div>
                  <label style={st.label}>희망 매각 금액 (만원) <span style={{ color: "var(--lt-red)" }}>*</span></label>
                  <input
                    type="number"
                    value={form.asking_price}
                    onChange={(e) => update("asking_price", e.target.value)}
                    placeholder="예: 15000"
                    required
                    min={1}
                    style={st.input}
                  />
                </div>
                <div>
                  <label style={st.label}>연락 가능 시간대</label>
                  <input
                    value={form.contact_hours}
                    onChange={(e) => update("contact_hours", e.target.value)}
                    placeholder="예: 평일 오전 9시~12시"
                    style={st.input}
                  />
                </div>
                <div style={{ gridColumn: m ? "1" : "1 / -1" }}>
                  <label style={st.label}>매각 이유 (선택)</label>
                  <input
                    value={form.sell_reason}
                    onChange={(e) => update("sell_reason", e.target.value)}
                    placeholder="예: 상속 후 활용 계획 없음"
                    style={st.input}
                  />
                </div>
                <div style={{ gridColumn: m ? "1" : "1 / -1" }}>
                  <label style={st.label}>특이사항 (선택)</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder="예: 도로 접해 있음, 분묘 있음 등 특이사항을 입력해 주세요"
                    rows={4}
                    style={{ ...st.input, resize: "vertical" as const, minHeight: 80 }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 안내 박스 */}
          <div style={{ background: "rgba(212,168,83,0.06)", border: "1px solid rgba(212,168,83,0.22)", borderLeft: "3px solid #D4A853", borderRadius: "var(--radius-md)", padding: "14px 18px" }}>
            <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700, color: "#D4A853" }}>{"🔒"} 개인정보 보호 안내</p>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.8 }}>
              <li>정확한 주소와 연락처는 인증된 중개사가 연결 신청 후에만 공개됩니다</li>
              <li>중개사 열람 시에는 시/군 지역만 노출됩니다</li>
              <li>연결 신청 시 카카오 알림톡으로 안내드립니다</li>
            </ul>
          </div>

          {error && <p style={st.error}>{error}</p>}

          <button
            type="submit"
            disabled={loading}
            style={{ padding: "14px", borderRadius: "var(--radius-md)", border: "none", background: "linear-gradient(135deg, #B8972E, #D4AF37)", color: "#0F172A", fontSize: 16, fontWeight: 700, cursor: "pointer", opacity: loading ? 0.6 : 1 }}
          >
            {loading ? "등록 중..." : "내 땅 등록하기"}
          </button>

        </form>
      </div>
      <AppFooter />
    </div>
  );
}

const st: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "var(--bg-primary)",
  },
  container: {
    flex: 1,
    maxWidth: 900,
    margin: "0 auto",
    width: "100%",
  },
  pageHeader: {
    padding: "28px 0 4px",
    borderBottom: "1px solid var(--border-light)",
    marginBottom: 28,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 800,
    color: "var(--text-primary)",
    margin: "0 0 6px",
  },
  pageDesc: {
    fontSize: 14,
    color: "var(--text-muted)",
    margin: 0,
  },
  section: {
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderLeft: "3px solid var(--lt-gold)",
    borderRadius: "var(--radius-xl)",
    overflow: "hidden",
    boxShadow: "var(--shadow-sm)",
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--lt-gold)",
    margin: 0,
    padding: "12px 20px",
    borderBottom: "1px solid var(--border-light)",
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    background: "rgba(212,168,83,0.04)",
  },
  sectionBody: {
    padding: 20,
  },
  label: {
    display: "block",
    fontSize: 14,
    fontWeight: 500,
    color: "var(--text-secondary)",
    marginBottom: 6,
  },
  input: {
    padding: "12px 14px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    fontSize: 16,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  error: {
    color: "var(--lt-red)",
    fontSize: 13,
    margin: 0,
    padding: "8px 12px",
    background: "rgba(239,68,68,0.08)",
    borderRadius: "var(--radius-md)",
  },
};
